import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button, DropdownList, Icons, PuzzleNav } from '@/js/libs/ui/src/V2';
import { PrivateContent, Roles, useIdentityUser, or, not } from '@/js/libs/use-auth';

import { SUPPORT_EMAIL } from 'src/constants';
import useAppLogout from 'src/hooks/useAppLogout';
import { LanguageKey } from 'src/types';
import { LanguageDropdown } from './LanguageDropdown';
import { ActionGroup, ActionGroupItem, Label, STATUS_ICON_SIZE_VALUE, StatusLink, Wrapper } from './styled';
import { useOngoingIncidentsCheck } from './useOngoingIncidentsCheck';

interface FooterProps {
  isCollapsedNav: boolean;
}

export const Footer = memo(function Footer({ isCollapsedNav }: FooterProps) {
  const { colors } = useTheme();
  const [t, i18n] = useTranslation(['backend', 'common']);
  const user = useIdentityUser();
  const logout = useAppLogout();

  const hasOngoingIncidents = useOngoingIncidentsCheck();

  const handleMailToSupport = useCallbackRef(() => {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  });

  const handleOpenUsersnap = useCallbackRef(() => {
    window.Usersnap.logEvent('open_usersnap');
  });

  return (
    <div>
      <PrivateContent roles={not(Roles.ANONYMOUS)}>
        <PuzzleNav.ItemSeparator standalone />
        <PuzzleNav.Item
          standalone
          icon={<Icons.Person />}
          title={user && user.firstName ? `${user.firstName.charAt(0)}. ${user.lastName}` : t('common:PROFILE')}
          itemKey='account'
          path='/account'
          dataTestId='profile-button'>
          <PrivateContent roles={not(Roles.SSO)}>
            <PuzzleNav.ItemSubLink title={t('components:MANAGE_ACCOUNT')} to='/account/edit-settings' dataTestId='manage-account-button' />
          </PrivateContent>
          <PrivateContent roles={or(Roles.ADMIN, Roles.COMPANY_ADMIN)}>
            <PuzzleNav.ItemSubLink title={t('components:COMPANY_EDIT')} to='/account/company' dataTestId='manage-company-button' />
            <PrivateContent roles={not(or(Roles.SSO, Roles.POSTADMANAGER))}>
              <PuzzleNav.ItemSubLink title={t('components:COMPANY_USERS')} to='/account/users' dataTestId='user-administration-button' />
            </PrivateContent>
          </PrivateContent>
          <PuzzleNav.ItemSubLink title={t('components:LOGOUT')} onClick={logout} dataTestId='user-logout-button' />
        </PuzzleNav.Item>
      </PrivateContent>
      <Wrapper isCollapsedNav={isCollapsedNav}>
        <StatusLink href='https://status.audienzz.ch/' target='_blank' rel='noopener noreferrer' isCollapsedNav={isCollapsedNav}>
          {hasOngoingIncidents ? (
            <Icons.Clear color={colors.critical100} filled size={STATUS_ICON_SIZE_VALUE} />
          ) : (
            <Icons.Check color={colors.positive100} filled size={STATUS_ICON_SIZE_VALUE} />
          )}
          {!isCollapsedNav && (
            <Label $nowrap={(i18n.language === LanguageKey.English || i18n.language === LanguageKey.Italian) && !hasOngoingIncidents}>
              {hasOngoingIncidents ? t('common:NOT_ALL_SYSTEMS_OPERATIONAL') : t('common:ALL_SYSTEMS_OPERATIONAL')}
            </Label>
          )}
        </StatusLink>
        {!isCollapsedNav && (
          <ActionGroup>
            <ActionGroupItem>
              <LanguageDropdown />
            </ActionGroupItem>
            <ActionGroupItem>
              <DropdownList
                minWidth={false}
                buttonComponent={
                  <Button iconOnly ghost>
                    <Icons.Headphones size={24} />
                  </Button>
                }>
                <DropdownList.DropdownItem onClick={handleMailToSupport}>{t('common:EMAIL')}</DropdownList.DropdownItem>
                <DropdownList.DropdownItem onClick={handleOpenUsersnap}>{t('common:USERSNAP')}</DropdownList.DropdownItem>
              </DropdownList>
            </ActionGroupItem>
          </ActionGroup>
        )}
      </Wrapper>
    </div>
  );
});
